import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { User } from '../../types';
import { UserService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { SaveButton, ConfirmDialog } from './common';

interface UserFormData {
  email: string;
  password: string;
  confirmPassword: string;
  currentPassword?: string;
  displayName?: string;
  role?: 'admin' | 'viewer';
}

export const UserEditor: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { currentUser, changePassword } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<UserFormData>({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      currentPassword: '',
      displayName: '',
      role: 'viewer',
    },
  });

  const password = watch('password');
  const isCreating = !user;
  const isSelfEdit = !isCreating && !!user && !!currentUser && user.id === currentUser.id;

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId || userId === 'create') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const users = await UserService.getUsers();
        const foundUser = users.find(u => u.id === userId);
        if (foundUser) {
          setUser(foundUser);
          reset({
            email: foundUser.email,
            password: '',
            confirmPassword: '',
            currentPassword: '',
            displayName: foundUser.displayName || '',
            role: foundUser.role || 'viewer',
          });
        }
      } catch (err) {
        console.error('ユーザー取得エラー:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, reset]);

  const handleSaveClick = () => {
    setSaveConfirmOpen(true);
  };

  const handleSaveConfirm = () => {
    handleSubmit(async (data) => {
      setSaving(true);
      try {
        if (isCreating) {
          await UserService.createUser(
            data.email,
            data.password,
            data.displayName || undefined,
            data.role || 'viewer'
          );
          setSaveConfirmOpen(false);
          navigate('/admin/users');
        } else {
          if (!user) return;
          if (isSelfEdit && data.currentPassword?.trim() && data.password?.trim()) {
            await changePassword(data.currentPassword, data.password);
          }
          await UserService.updateUser(user.id, {
            displayName: data.displayName || undefined,
            email: data.email,
            role: data.role || 'viewer',
          });
          setSaveConfirmOpen(false);
          navigate('/admin/users');
        }
      } catch (err) {
        console.error('ユーザー登録/更新エラー:', err);
        setSaveConfirmOpen(false);
      } finally {
        setSaving(false);
      }
    })();
  };

  const handleSaveCancel = () => {
    setSaveConfirmOpen(false);
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      setSaving(true);
      
      if (isCreating) {
        await UserService.createUser(
          data.email,
          data.password,
          data.displayName || undefined,
          data.role || 'viewer'
        );
        navigate('/admin/users');
      } else {
        if (!user) return;
        if (isSelfEdit && data.currentPassword?.trim() && data.password?.trim()) {
          await changePassword(data.currentPassword, data.password);
        }
        await UserService.updateUser(user.id, {
          displayName: data.displayName || undefined,
          email: data.email,
          role: data.role || 'viewer',
        });
        navigate('/admin/users');
      }
    } catch (err) {
      console.error('ユーザー登録/更新エラー:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => navigate('/admin/users')}
            sx={{
              backgroundColor: '#FFFFFF',
              color: '#000000',
              width: 40,
              height: 40,
              borderRadius: '50%',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ fontSize: '24px', fontWeight: 700, color: '#000000' }}
          >
            {isCreating ? '新規登録' : '編集'}
            {!isCreating && user && `: ${user.displayName || '-'} ${user.email}`}
          </Typography>
        </Box>
        <SaveButton
          onClick={handleSaveClick}
          saving={saving}
        />
      </Box>

      {/* フォーム */}
      <Box 
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ p: 2, width: '800px' }}
      >
        <Typography 
          variant="subtitle1" 
          gutterBottom 
          sx={{ fontWeight: 600, mb: 2, fontSize: '18px' }}
        >
          ユーザー情報
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="名前"
                placeholder="名前"
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#D0D0D0',
                    },
                    '& .MuiInputBase-input': {
                      color: '#000000',
                    },
                    '& .MuiInputLabel-root': {
                      color: '#000000',
                    },
                  },
                }}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: 'メールアドレスは必須です',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: '有効なメールアドレスを入力してください',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="メールアドレス"
                placeholder="メールアドレス"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={!isCreating}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#D0D0D0',
                    },
                    '& .MuiInputBase-input': {
                      color: '#000000',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#EBEBEB',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '& .MuiInputBase-input': {
                        color: '#000000',
                      },
                    },
                  },
                  '& .MuiInputLabel-root.Mui-disabled': {
                    color: '#000000',
                  },
                }}
                InputProps={{
                  inputProps: {
                    style: {
                      WebkitTextFillColor: '#000000',
                      color: '#000000',
                    },
                  },
                }}
              />
            )}
          />

          {isSelfEdit && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                パスワード変更（本人のみ）
              </Typography>
              <Box>
                <Controller
                  name="currentPassword"
                  control={control}
                  rules={{
                    validate: (v) => {
                      if (password?.trim()) return !!v?.trim() || '現在のパスワードを入力してください';
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="現在のパスワード"
                      placeholder="変更する場合のみ入力"
                      type={showCurrentPassword ? 'text' : 'password'}
                      error={!!errors.currentPassword}
                      helperText={errors.currentPassword?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          '& fieldset': { borderColor: '#D0D0D0' },
                        },
                      }}
                    />
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showCurrentPassword}
                      onChange={(e) => setShowCurrentPassword(e.target.checked)}
                      disableRipple
                      icon={
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#FFFFFF', border: '1px solid #D0D0D0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                      }
                      checkedIcon={
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#F49121', border: '1px solid #F49121', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
                        </Box>
                      }
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'transparent' } }}
                    />
                  }
                  label="現在のパスワードを表示する"
                  sx={{ mt: 0.5, cursor: 'pointer' }}
                />
              </Box>
              <Box>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    validate: (v) => {
                      if (!v?.trim()) return true;
                      return v.length >= 6 || 'パスワードは6文字以上で入力してください';
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="新しいパスワード"
                      placeholder="変更する場合のみ入力"
                      type={showPassword ? 'text' : 'password'}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          '& fieldset': { borderColor: '#D0D0D0' },
                        },
                      }}
                    />
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                      disableRipple
                      icon={
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#FFFFFF', border: '1px solid #D0D0D0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                      }
                      checkedIcon={
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#F49121', border: '1px solid #F49121', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
                        </Box>
                      }
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'transparent' } }}
                    />
                  }
                  label="新しいパスワードを表示する"
                  sx={{ mt: 0.5, cursor: 'pointer' }}
                />
              </Box>
              <Box>
                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!password?.trim()) return true;
                      return value === password || 'パスワードが一致しません';
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="新しいパスワード (確認用)"
                      placeholder="変更する場合のみ入力"
                      type={showConfirmPassword ? 'text' : 'password'}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          '& fieldset': { borderColor: '#D0D0D0' },
                        },
                      }}
                    />
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showConfirmPassword}
                      onChange={(e) => setShowConfirmPassword(e.target.checked)}
                      disableRipple
                      icon={
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#FFFFFF', border: '1px solid #D0D0D0', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                      }
                      checkedIcon={
                        <Box sx={{ width: 20, height: 20, backgroundColor: '#F49121', border: '1px solid #F49121', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
                        </Box>
                      }
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'transparent' } }}
                    />
                  }
                  label="パスワードを表示する"
                  sx={{ mt: 0.5, cursor: 'pointer' }}
                />
              </Box>
            </>
          )}

          {isCreating && (
            <>
              <Box>
                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: 'パスワードは必須です',
                    minLength: {
                      value: 6,
                      message: 'パスワードは6文字以上で入力してください',
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="パスワード"
                      placeholder="パスワード"
                      type={showPassword ? 'text' : 'password'}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          '& fieldset': {
                            borderColor: '#D0D0D0',
                          },
                        },
                      }}
                    />
                  )}
                />
                 <FormControlLabel
                   control={
                     <Checkbox
                       checked={showPassword}
                       onChange={(e) => setShowPassword(e.target.checked)}
                       disableRipple
                       icon={
                         <Box
                           sx={{
                             width: 20,
                             height: 20,
                             backgroundColor: '#FFFFFF',
                             border: '1px solid #D0D0D0',
                             borderRadius: '4px',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                           }}
                         />
                       }
                       checkedIcon={
                         <Box
                           sx={{
                             width: 20,
                             height: 20,
                             backgroundColor: '#F49121',
                             border: '1px solid #F49121',
                             borderRadius: '4px',
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                           }}
                         >
                           <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
                         </Box>
                       }
                       sx={{
                         cursor: 'pointer',
                         '&:hover': {
                           backgroundColor: 'transparent',
                         },
                       }}
                     />
                   }
                   label="パスワードを表示する"
                   sx={{ mt: 0.5, cursor: 'pointer' }}
                 />
              </Box>

              <Box>
                <Controller
                  name="confirmPassword"
                  control={control}
                  rules={{
                    required: 'パスワード確認は必須です',
                    validate: (value) =>
                      value === password || 'パスワードが一致しません',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="パスワード (確認用)"
                      placeholder="パスワード (確認用)"
                      type={showConfirmPassword ? 'text' : 'password'}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          '& fieldset': {
                            borderColor: '#D0D0D0',
                          },
                        },
                      }}
                    />
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showConfirmPassword}
                      onChange={(e) => setShowConfirmPassword(e.target.checked)}
                      disableRipple
                      icon={
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: '#FFFFFF',
                            border: '1px solid #D0D0D0',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        />
                      }
                      checkedIcon={
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: '#F49121',
                            border: '1px solid #F49121',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
                        </Box>
                      }
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    />
                  }
                  label="パスワードを表示する"
                  sx={{ mt: 0.5, cursor: 'pointer' }}
                />
              </Box>
            </>
          )}

          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>権限</InputLabel>
                <Select
                  {...field}
                  label="権限"
                  error={!!errors.role}
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: '#FFFFFF',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#FFFFFF',
                    },
                    '& fieldset': {
                      borderColor: '#D0D0D0',
                    },
                  }}
                >
                  <MenuItem value="admin">管理者</MenuItem>
                  <MenuItem value="viewer">閲覧者</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Box>
      </Box>

      <ConfirmDialog
        open={saveConfirmOpen}
        onClose={handleSaveCancel}
        onConfirm={handleSaveConfirm}
        message={
          <>
            {isCreating ? '新規登録' : '編集'}内容で保存します。
            <br />
            よろしいですか?
          </>
        }
        confirmText="保存"
        confirmColor="#FF9800"
        confirmHoverColor="#F57C00"
        confirmDisabled={saving}
        reverseButtons={true}
      />
    </Box>
  );
};

